import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import {
  NOTE_TYPE_COLOURS,
  NOTE_TYPE_LABEL_COLOURS
} from 'src/components/Constants';
import { getRecordDate, getRecordTitle } from './forms/formService';
import {
  PEDIGREE_RECORD_SURFACES,
  recordCardTint,
  recordCardTintBorder
} from './recordCardStyles';

const BodyRow = ({ label, value, multiline }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1,
      py: 0.4,
      alignItems: multiline ? 'flex-start' : 'baseline'
    }}
  >
    <Typography
      variant="body1"
      sx={{
        fontWeight: 'bold',
        minWidth: 130,
        flexShrink: 0,
        pt: multiline ? 0.2 : 0
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
        wordBreak: 'break-word'
      }}
    >
      {value}
    </Typography>
  </Box>
);

const RecordBody = ({ record, staffMemberOptions, dateFormat }) => {
  const fmt = (d) => (d ? moment(d).format(dateFormat) : '');
  const findStaff = (id) =>
    staffMemberOptions?.find((s) => s.value === id)?.label || id;

  switch (record.noteType) {
    case 'Appointment':
      return (
        <Box>
          {record.appointmentDate && (
            <BodyRow label="Date" value={fmt(record.appointmentDate)} />
          )}
          {record.appointmentTime && (
            <BodyRow
              label="Time"
              value={moment(record.appointmentTime).format('HH:mm')}
            />
          )}
          {record.clinicName && (
            <BodyRow label="Clinic" value={record.clinicName} />
          )}
          {record.appointmentType && (
            <BodyRow label="Type" value={record.appointmentType} />
          )}
          {record.appointmentStatus && (
            <BodyRow label="Status" value={record.appointmentStatus} />
          )}
          {record.location && (
            <BodyRow label="Location" value={record.location} />
          )}
          {record.staffMember?.length > 0 && (
            <BodyRow
              label="Staff"
              value={record.staffMember.map(findStaff).join(', ')}
            />
          )}
          {record.appointmentNotes && (
            <BodyRow label="Notes" value={record.appointmentNotes} multiline />
          )}
        </Box>
      );

    case 'Referral':
      return (
        <Box>
          {record.referralDate && (
            <BodyRow label="Date" value={fmt(record.referralDate)} />
          )}
          {record.referralType && (
            <BodyRow label="Type" value={record.referralType} />
          )}
          {record.referralTo && (
            <BodyRow label="Referred To" value={record.referralTo} />
          )}
          {record.referredBy && (
            <BodyRow label="Referred By" value={record.referredBy} />
          )}
          {record.referralStatus && (
            <BodyRow label="Status" value={record.referralStatus} />
          )}
          {record.referralTriage && (
            <BodyRow label="Triage" value={record.referralTriage} />
          )}
          {record.referralReason && (
            <BodyRow label="Reason" value={record.referralReason} multiline />
          )}
          {record.referralNotes && (
            <BodyRow label="Notes" value={record.referralNotes} multiline />
          )}
          {record.dateClosed && (
            <BodyRow label="Date Closed" value={fmt(record.dateClosed)} />
          )}
          {record.reasonForClosure && (
            <BodyRow
              label="Reason for Closure"
              value={record.reasonForClosure}
              multiline
            />
          )}
        </Box>
      );

    case 'Test':
      return (
        <Box>
          {record.testName && (
            <BodyRow label="Test Name" value={record.testName} />
          )}
          {record.testType && <BodyRow label="Type" value={record.testType} />}
          {record.dateRequested && (
            <BodyRow label="Date Requested" value={fmt(record.dateRequested)} />
          )}
          {record.dateResultDue && (
            <BodyRow label="Date Due" value={fmt(record.dateResultDue)} />
          )}
          {record.dateResultReceived && (
            <BodyRow
              label="Date Received"
              value={fmt(record.dateResultReceived)}
            />
          )}
          {record.lab && <BodyRow label="Lab" value={record.lab} />}
          {record.ageWhenTested != null && (
            <BodyRow label="Age When Tested" value={record.ageWhenTested} />
          )}
          {record.testResult && (
            <BodyRow label="Result" value={record.testResult} multiline />
          )}
          {record.testNotes && (
            <BodyRow label="Notes" value={record.testNotes} multiline />
          )}
        </Box>
      );

    case 'Clinical Note':
      return (
        <Box>
          {record.clinicalNoteDate && (
            <BodyRow label="Date" value={fmt(record.clinicalNoteDate)} />
          )}
          {record.clinician && (
            <BodyRow label="Clinician" value={record.clinician} />
          )}
          {record.note && (
            <BodyRow label="Note" value={record.note} multiline />
          )}
        </Box>
      );

    case 'Sample':
      return (
        <Box>
          {record.dateTaken && (
            <BodyRow label="Date Taken" value={fmt(record.dateTaken)} />
          )}
          {record.sampleType && (
            <BodyRow label="Type" value={record.sampleType} />
          )}
          {record.laboratory && (
            <BodyRow label="Laboratory" value={record.laboratory} />
          )}
          {record.sampleStatus && (
            <BodyRow label="Status" value={record.sampleStatus} />
          )}
          {record.statusDate && (
            <BodyRow label="Status Date" value={fmt(record.statusDate)} />
          )}
          {record.sampleHeldBy && (
            <BodyRow label="Held By" value={record.sampleHeldBy} />
          )}
          {record.trackingId && (
            <BodyRow label="Tracking ID" value={record.trackingId} />
          )}
        </Box>
      );

    case 'Treatment':
      return (
        <Box>
          {record.treatmentDate && (
            <BodyRow label="Start Date" value={fmt(record.treatmentDate)} />
          )}
          {record.treatmentEndDate && (
            <BodyRow label="End Date" value={fmt(record.treatmentEndDate)} />
          )}
          {record.treatment && (
            <BodyRow label="Treatment" value={record.treatment} multiline />
          )}
          {record.comment && (
            <BodyRow label="Comment" value={record.comment} multiline />
          )}
        </Box>
      );

    case 'Contact':
      return (
        <Box>
          {record.contactDate && (
            <BodyRow label="Date" value={fmt(record.contactDate)} />
          )}
          {record.contactType && (
            <BodyRow label="Type" value={record.contactType} />
          )}
          {record.contactMode && (
            <BodyRow label="Mode" value={record.contactMode} />
          )}
          {record.contactStatus && (
            <BodyRow label="Status" value={record.contactStatus} />
          )}
          {record.reasonForContact && (
            <BodyRow label="Reason" value={record.reasonForContact} multiline />
          )}
          {record.staffMember?.length > 0 && (
            <BodyRow
              label="Staff"
              value={record.staffMember.map(findStaff).join(', ')}
            />
          )}
          {record.contactNotes && (
            <BodyRow label="Notes" value={record.contactNotes} multiline />
          )}
        </Box>
      );

    case 'Consent':
      return (
        <Box>
          {(record.consentDate || record.consentRequestDate) && (
            <BodyRow
              label="Date"
              value={fmt(record.consentDate || record.consentRequestDate)}
            />
          )}
          {record.consentRequestDate && record.consentDate && (
            <BodyRow
              label="Request Date"
              value={fmt(record.consentRequestDate)}
            />
          )}
          {record.consentType && (
            <BodyRow label="Type" value={record.consentType} />
          )}
          {record.response && (
            <BodyRow label="Response" value={record.response} />
          )}
          {record.consentGivenBy && (
            <BodyRow label="Given By" value={record.consentGivenBy} />
          )}
          {record.expiryDate && (
            <BodyRow label="Expiry" value={fmt(record.expiryDate)} />
          )}
          {record.consentNotes && (
            <BodyRow label="Notes" value={record.consentNotes} multiline />
          )}
        </Box>
      );

    case 'Address':
      return (
        <Box>
          {record.addressLine1 && (
            <BodyRow label="Line 1" value={record.addressLine1} />
          )}
          {record.addressLine2 && (
            <BodyRow label="Line 2" value={record.addressLine2} />
          )}
          {(record.town || record.city) && (
            <BodyRow label="Town/City" value={record.town || record.city} />
          )}
          {record.postCode && (
            <BodyRow label="Postcode" value={record.postCode} />
          )}
          {record.primaryAddress && <BodyRow label="Primary" value="Yes" />}
          {record.correspondenceAddress && (
            <BodyRow label="Correspondence" value="Yes" />
          )}
        </Box>
      );

    case 'Tissue Pathology':
      return (
        <Box>
          {record.tissueSite && (
            <BodyRow label="Tissue Site" value={record.tissueSite} />
          )}
          {record.morphology && (
            <BodyRow label="Morphology" value={record.morphology} />
          )}
          {record.pathReportDate && (
            <BodyRow label="Report Date" value={fmt(record.pathReportDate)} />
          )}
          {record.staffMember?.length > 0 && (
            <BodyRow
              label="Staff"
              value={record.staffMember.map(findStaff).join(', ')}
            />
          )}
          {record.pathologyDoctor && (
            <BodyRow
              label="Pathologist"
              value={findStaff(record.pathologyDoctor)}
            />
          )}
        </Box>
      );

    case 'Disease':
      return (
        <Box>
          {record.id && <BodyRow label="ID" value={record.id} />}
          {record.age && (
            <BodyRow label="Age at diagnosis" value={record.age} />
          )}
          {record.dateReported && (
            <BodyRow label="Date reported" value={fmt(record.dateReported)} />
          )}
          <BodyRow
            label="Clinically confirmed"
            value={record.clinicallyConfirmed ? 'Yes' : 'No'}
          />
        </Box>
      );

    case 'Phenotype':
      return (
        <Box>
          {record.id && <BodyRow label="ID" value={record.id} />}
          {record.recordedAge && (
            <BodyRow label="Age reported" value={record.recordedAge} />
          )}
          {record.dateRecorded && (
            <BodyRow label="Date recorded" value={fmt(record.dateRecorded)} />
          )}
          <BodyRow
            label="Clinically confirmed"
            value={record.clinicallyConfirmed ? 'Yes' : 'No'}
          />
        </Box>
      );

    case 'Genotype':
      return (
        <Box>
          {record.id && <BodyRow label="ID" value={record.id} />}
          {record.carrierStatus && (
            <BodyRow label="Carrier status" value={record.carrierStatus} />
          )}
          {record.recordedAge && (
            <BodyRow label="Age reported" value={record.recordedAge} />
          )}
          {record.dateRecorded && (
            <BodyRow label="Date recorded" value={fmt(record.dateRecorded)} />
          )}
          <BodyRow
            label="Clinically confirmed"
            value={record.clinicallyConfirmed ? 'Yes' : 'No'}
          />
        </Box>
      );

    case 'Risk Score': {
      let summary = '';
      if (record.recordType === 'gail' && record.gailRiskScore != null) {
        summary = `Risk score: ${record.gailRiskScore}`;
      } else if (record.recordType === 'canrisk') {
        try {
          const pr = record.canriskResult?.pedigree_result?.[0];
          const tenYr =
            pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
          const lifetime =
            pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
          if (tenYr != null && lifetime != null) {
            summary = `10yr: ${tenYr}% | Lifetime: ${lifetime}%`;
          }
        } catch (_) {
          /* ignore */
        }
        if (!summary) {
          summary = record.canriskResult
            ? 'Scores calculated'
            : 'Pedigree downloaded';
        }
      } else if (record.recordType === 'questionnaire') {
        summary = 'Submitted from family history questionnaire';
      }
      return (
        <Box>
          <BodyRow label="Type" value={record.riskLabel} />
          {summary && <BodyRow label="Result" value={summary} />}
        </Box>
      );
    }

    default:
      return null;
  }
};

const RecordCard = ({
  record,
  isExpanded,
  isFocused,
  onToggle,
  onFocus,
  cardRef,
  dateFormat,
  staffMemberOptions,
  surfaces = PEDIGREE_RECORD_SURFACES
}) => {
  const { theme: cardTheme } = React.useContext(ThemeContext);
  const accentColor = cardTheme?.accent || '#1976d2';

  const title = getRecordTitle(record);
  const date = getRecordDate(record);
  const typeColor = NOTE_TYPE_COLOURS[record.noteType] || '#888';
  const labelColor = surfaces
    ? NOTE_TYPE_LABEL_COLOURS[record.noteType] || typeColor
    : typeColor;

  return (
    <Box
      ref={cardRef}
      role="listitem"
      aria-expanded={isExpanded}
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: isFocused
          ? accentColor
          : surfaces
          ? recordCardTintBorder(typeColor)
          : '#e0e0e0',
        borderLeft: `3px solid ${typeColor}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: surfaces ? recordCardTint(typeColor) : '#fff',
        transition: surfaces
          ? 'border-color 0.15s, transform .15s ease, box-shadow .15s ease'
          : 'border-color 0.15s',
        ...(surfaces && {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(31, 42, 51, .13)'
          }
        })
      }}
    >
      <Box
        onClick={(e) => {
          onFocus();
          onToggle(record._id, e);
        }}
        sx={{
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          userSelect: 'none'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: labelColor,
              lineHeight: 1
            }}
          >
            {record.noteType}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {date && moment(date).isValid() && (
              <Typography
                variant="body1"
                sx={{ flexShrink: 0, fontWeight: 700 }}
              >
                {moment(date).format(dateFormat)}
              </Typography>
            )}
            <IconButton
              size="small"
              tabIndex={-1}
              aria-label={isExpanded ? 'Collapse record' : 'Expand record'}
              sx={{ ml: 0.5, flexShrink: 0 }}
            >
              {isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {title}
          </Typography>
          {record.clinicallyConfirmed && (
            <CheckCircleOutlineIcon
              sx={{ color: 'green', fontSize: 18, flexShrink: 0 }}
            />
          )}
        </Box>
      </Box>

      <Collapse in={isExpanded} unmountOnExit>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <RecordBody
            record={record}
            staffMemberOptions={staffMemberOptions}
            dateFormat={dateFormat}
          />
          {record.fileAttachments?.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Attachments
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
              >
                {record.fileAttachments.map((f) => (
                  <Chip key={f} label={f} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RecordCard;
